import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, Modal, Field, Badge, Empty } from "@/components/Kit";
import { UserPlus, Pencil, Trash2, KeyRound, ShieldCheck, GraduationCap } from "lucide-react";

const ROLE = {
  school_admin: { label: "School Admin", color: "#4F46E5", icon: ShieldCheck },
  teacher: { label: "Teacher", color: "#06B6D4", icon: GraduationCap },
};
const genPw = () => Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6);
const EMPTY = { name: "", email: "", phone: "", password: genPw(), role: "teacher", notify: true };

export default function Staff() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | "new" | staff object
  const { data: staff = [], isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: async () => (await api.get("/school/staff")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["staff"] });

  const create = useMutation({
    mutationFn: async (body) => (await api.post("/school/staff", body)).data,
    onSuccess: (u) => { invalidate(); setModal(null); toast.success(`${u.name} added${u.phone ? " · credentials sent on WhatsApp" : ""}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/school/staff/${id}`, body)).data,
    onSuccess: () => { invalidate(); setModal(null); toast.success("Staff updated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/staff/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("Staff removed"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="staff-page">
      <PageHeader
        title="Staff Users"
        subtitle="Create login accounts for teachers and co-admins"
        action={<button className="ep-btn" onClick={() => setModal("new")} data-testid="add-staff-btn"><UserPlus size={16} /> Add Staff</button>}
      />

      <div className="ep-card overflow-hidden">
        {isLoading ? (
          <p className="px-4 py-8 text-center text-slate-500">Loading…</p>
        ) : staff.length ? (
          staff.map((u) => {
            const r = ROLE[u.role] || ROLE.teacher;
            return (
              <div key={u.id} className="flex items-center justify-between gap-3 border-b border-slate-800/50 px-4 py-3" data-testid={`staff-row-${u.email}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium text-white">
                    {u.name} {u.self && <span className="text-[10px] font-normal text-slate-500">(you)</span>}
                  </p>
                  <p className="truncate text-xs text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge color={r.color}><r.icon size={11} className="mr-1" />{r.label}</Badge>
                  <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white" onClick={() => setModal(u)} data-testid={`staff-edit-${u.email}`}><Pencil size={14} /></button>
                  {!u.self && (
                    <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-rose-400" onClick={() => { if (window.confirm(`Remove ${u.name}?`)) remove.mutate(u.id); }} data-testid={`staff-del-${u.email}`}><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <Empty>No staff users yet</Empty>
        )}
      </div>

      {modal === "new" && (
        <StaffForm
          title="Add staff user"
          initial={{ ...EMPTY, password: genPw() }}
          busy={create.isPending}
          showPassword
          onClose={() => setModal(null)}
          onSubmit={(v) => create.mutate(v)}
        />
      )}
      {modal && modal !== "new" && (
        <StaffForm
          title={`Edit ${modal.name}`}
          initial={{ id: modal.id, name: modal.name, phone: modal.phone, role: modal.role, password: "", self: modal.self }}
          busy={update.isPending}
          editing
          onClose={() => setModal(null)}
          onSubmit={(v) => update.mutate(v)}
        />
      )}
    </div>
  );
}

function StaffForm({ title, initial, busy, onClose, onSubmit, editing, showPassword }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Modal open onClose={onClose} title={title} testid="staff-modal">
      <div className="space-y-3">
        <Field label="Full name" value={f.name || ""} onChange={set("name")} data-testid="staff-name" autoFocus />
        {!editing && <Field label="Email (used to log in)" type="email" value={f.email || ""} onChange={set("email")} data-testid="staff-email" />}
        <Field label="Mobile (for WhatsApp login details)" value={f.phone || ""} onChange={set("phone")} data-testid="staff-phone" maxLength={10} placeholder="10-digit" />
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Role</span>
          <select className="ep-input" value={f.role || "teacher"} onChange={set("role")} data-testid="staff-role" disabled={f.self}>
            <option value="teacher">Teacher</option>
            <option value="school_admin">School Admin</option>
          </select>
          {f.self && <span className="mt-1 block text-[11px] text-slate-500">You can’t change your own role.</span>}
        </label>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">{editing ? "New password (leave blank to keep)" : "Temporary password"}</span>
            {(showPassword || editing) && (
              <button type="button" className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300" onClick={() => setF({ ...f, password: genPw() })} data-testid="staff-genpw">
                <KeyRound size={11} /> Generate
              </button>
            )}
          </div>
          <input className="ep-input mt-1.5 font-mono" value={f.password || ""} onChange={set("password")} data-testid="staff-password" placeholder={editing ? "••••••" : ""} />
        </div>
        {!editing && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" className="h-3.5 w-3.5 accent-indigo-500" checked={!!f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} data-testid="staff-notify" />
            Send login details to their mobile on WhatsApp
          </label>
        )}
        <button className="ep-btn w-full justify-center" disabled={busy || !f.name || (!editing && (!f.email || (f.password || "").length < 6))}
          onClick={() => onSubmit(f)} data-testid="staff-save">
          {busy ? "Saving…" : editing ? "Save changes" : "Create staff user"}
        </button>
      </div>
    </Modal>
  );
}
